// Route Code Edited 03/25/26
/**
 * Route T&C/Checkbox Handler
 * 
 * PROBLEM: Route's native checkout process doesn't validate custom checkboxes
 * (e.g., age verification, terms & conditions) before proceeding to checkout.
 * 
 * SOLUTION: Intercepts clicks on Route's checkout button to validate required 
 * checkboxes. If validation fails, redirects to the original checkout button
 * instead, preventing checkout until requirements are met.
 * 
 * Configuration Selectors:
 * - origCheckout: Original checkout button (excludes Route's cloned button)
 * - routeCheckout: Route's cloned checkout button  
 * - routeCWC: Route's "Continue Without Coverage" link
 * - cartContainer: Optional array of cart container selectors to scope search
 *   • Limits checkbox/button search to specific cart areas
 *   • Example: ['#cart-drawer', '.cart-page-checkout-container']
 * - requiredCheckboxes: Array of checkbox selectors that must be checked
 *   • Example: ['#age-verification', '.terms-checkbox input', '[name="agree"]']
 */
const config = {
  selectors: {
    origCheckout: '[data-original-display]',
    routeCheckout: '[data-route-cloned-button]',
    routeCWC: '.route-continue-without-coverage-link',
    cartContainer: ['.drawer__footer', '#MainContent'],
    requiredCheckboxes: ['#CartAgree', '#CartPageAgree']
  }
};

const { selectors } = config;

// Cache combined selector to avoid recreating on every event
const ROUTE_BUTTON_SELECTOR = `${selectors.routeCheckout}, ${selectors.routeCWC}`;

// Intercept both 'mousedown' and 'click' in capture phase.
// Route may bind its checkout logic to either event, so we cover both.
// The original button redirect only fires on 'click' to avoid double-triggering.
["mousedown", "click"].forEach(eventType => {
  document.addEventListener(eventType, (event) => {
    // Check if the event was on a Route checkout element
    const clickedRouteButton = event.target.closest(ROUTE_BUTTON_SELECTOR);
    if (!clickedRouteButton) return;

    // Determine search scope (cart container or entire document)
    // .find() returns the matched selector STRING, so we must query the DOM with it
    const containerSelector = selectors.cartContainer.find(selector => clickedRouteButton.closest(selector));
    const searchContext = containerSelector ? document.querySelector(containerSelector) ?? document : document;

    // Check if all required checkboxes are checked
    const allCheckboxesChecked = selectors.requiredCheckboxes.every(checkboxSelector => {
      const checkbox = searchContext.querySelector(checkboxSelector);
      // Treats missing checkboxes as valid (passes validation if not found in DOM)
      // This prevents blocking checkout if optional checkboxes aren't rendered
      return checkbox?.checked ?? true;
    });

    // Allow Route's normal behavior if validation passes
    if (allCheckboxesChecked) return;

    // Block Route's checkout action on both event types
    event.preventDefault();
    event.stopImmediatePropagation();

    // Only redirect to the original checkout button on 'click' (not 'mousedown')
    // to avoid triggering it twice
    if (eventType !== "click") return;

    // Validation failed - find original checkout button to redirect to
    const allOriginalButtons = searchContext.querySelectorAll(selectors.origCheckout);

    if (allOriginalButtons.length === 0) {
      console.error(`Route checkout redirect failed: No original checkout button found matching selector: ${selectors.origCheckout}`);
      return;
    }
    if (allOriginalButtons.length > 1) {
      console.warn(`Route checkout redirect: Found ${allOriginalButtons.length} original checkout buttons matching selector: ${selectors.origCheckout}. Clicking the first one.`);
    }

    // Trigger original checkout (will show native validation messages)
    allOriginalButtons[0].click();
  }, { capture: true });
});
// End Route Code